function dirOf(side) {
    return side === "long" ? 1 : -1;
}
function roundMoney(value, currency) {
    return currency === "VND" ? Math.round(value) : value;
}
export class ExecutionSimulator {
    slippageTicks;
    brokerFeeRate;
    commissionPerContract;
    taxRate;
    constructor(options = {}) {
        this.slippageTicks = options.slippageTicks ?? 0;
        this.brokerFeeRate = options.brokerFeeRate ?? 0.001;
        this.commissionPerContract = options.commissionPerContract ?? 3000;
        this.taxRate = options.taxRate ?? 0.001;
    }
    /**
     * S2: live-update pricing knobs without recreating the broker (positions,
     * orders and WS subscriptions survive). Prospective-only: booked fills keep
     * old values. Returns the effective options for UI logging.
     */
    updateOptions(patch) {
        if (patch.slippageTicks !== undefined)
            this.slippageTicks = patch.slippageTicks;
        if (patch.brokerFeeRate !== undefined)
            this.brokerFeeRate = patch.brokerFeeRate;
        if (patch.commissionPerContract !== undefined)
            this.commissionPerContract = patch.commissionPerContract;
        if (patch.taxRate !== undefined)
            this.taxRate = patch.taxRate;
        return {
            slippageTicks: this.slippageTicks,
            brokerFeeRate: this.brokerFeeRate,
            commissionPerContract: this.commissionPerContract,
            taxRate: this.taxRate,
        };
    }
    executeFill(request, rules) {
        const { order, basePrice, quantity, timestamp, positionBasis, exitReason, ambiguousIntrabarFill, tieBreakReason } = request;
        const fillPrice = this.applySlippage(basePrice, order.side, rules);
        const commission = this.calculateCommission(fillPrice, quantity, rules);
        const tax = this.calculateTax(fillPrice, quantity, rules, order.side);
        let netPnl = 0;
        if (positionBasis) {
            const multiplier = rules.contractMultiplier ?? 1;
            const grossPnl = (fillPrice - positionBasis.entryPrice) * dirOf(positionBasis.side) * quantity * multiplier;
            netPnl = grossPnl - commission - tax;
        }
        return {
            orderId: order.id,
            symbol: order.symbol,
            side: order.side,
            fillPrice,
            fillQuantity: quantity,
            commission,
            tax,
            pnl: netPnl,
            exitReason,
            timestamp,
            ambiguousIntrabarFill,
            tieBreakReason,
        };
    }
    applySlippage(basePrice, side, rules) {
        if (this.slippageTicks === 0)
            return basePrice;
        const tick = rules.getTickSize(basePrice);
        const slip = this.slippageTicks * tick;
        const price = side === "buy" ? basePrice + slip : basePrice - slip;
        return Number(price.toFixed(4));
    }
    calculateCommission(price, quantity, rules) {
        if (rules.profile === "VN_DERIVATIVES") {
            return roundMoney(quantity * this.commissionPerContract, rules.baseCurrency);
        }
        return roundMoney(price * quantity * this.brokerFeeRate, rules.baseCurrency);
    }
    calculateTax(price, quantity, rules, side) {
        if (rules.profile === "VN_SPOT") {
            if (side !== "sell")
                return 0;
            return roundMoney(price * quantity * this.taxRate, rules.baseCurrency);
        }
        if (rules.profile === "VN_DERIVATIVES") {
            const multiplier = rules.contractMultiplier ?? 100000;
            const im = rules.initialMarginRate ?? 0.175;
            const taxValue = price * multiplier * quantity * (im / 2) * this.taxRate;
            return roundMoney(taxValue, rules.baseCurrency);
        }
        return 0;
    }
    checkIntrabarAmbiguity(tpPrice, slPrice, bar, side) {
        if (side === "long") {
            return bar.low <= slPrice && bar.high >= tpPrice;
        }
        return bar.high >= slPrice && bar.low <= tpPrice;
    }
    estimateOrderCost(order, price, rules) {
        const multiplier = rules.contractMultiplier ?? 1;
        const estimatedCost = roundMoney(price * order.quantity * multiplier, rules.baseCurrency);
        const commission = this.calculateCommission(price, order.quantity, rules);
        const estimatedTax = this.calculateTax(price, order.quantity, rules, order.side);
        const totalOutflow = order.side === "buy"
            ? estimatedCost + commission + estimatedTax
            : estimatedCost - commission - estimatedTax;
        return {
            estimatedCost,
            commission,
            estimatedTax,
            totalOutflow,
        };
    }
}
